import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Box,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  message: string;
  title?: string;
  loading?: boolean;
  confirmLabel?: string;
  loadingLabel?: string;
}

const ConfirmationModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  message, 
  title = "Confirm Action",
  loading = false,
  confirmLabel = "Confirm",
  loadingLabel = "Deleting..."
}: ConfirmationModalProps) => {
  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          minWidth: '400px',
        },
      }}
    >
      <DialogTitle sx={{ 
        p: 2.5, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton 
          onClick={onClose} 
          size="small"
          disabled={loading}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body1">{message}</Typography>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        bgcolor: 'background.paper',
        borderTop: '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          size="medium"
          color="secondary"
          disabled={loading}
          sx={{ 
            textTransform: 'none',
            borderRadius: 1,
            px: 2.5
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          color="secondary" 
          variant="contained"
          size="medium"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ 
            textTransform: 'none',
            borderRadius: 1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            px: 2.5
          }}
        >
          {loading ? loadingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal; 